import { queryItemListByInternalItemIds } from '@knowledge/common/components/ItemSelectV2/api';
import { cloneDeep, difference } from 'lodash-es';

/**
 * 当用户进入知识配置的页面的时候需要商品数据进行一下缓存，避免多次调用接口
 * 在 src/views/Knowledge/Detail/index.tsx 中需要清空缓存
 */

let internalIdMap: Record<string, ItemSelectV2.Item> = {};

type Source = {
  itemInternalId: string;
  skus: {
    skuId: string;
    [k: string]: any;
  }[];
  [k: string]: any;
};

const getSpec = (source: Source[]): ItemSelectV2.Item[] => {
  const result: ItemSelectV2.Item[] = [];
  for (let i = 0; i < source.length; i++) {
    const item = source[i];
    const { itemInternalId, skus } = item;
    const spu = internalIdMap[itemInternalId];
    if (spu) {
      const copySpu = cloneDeep(spu);
      if (copySpu.skuInfo.length > 0) {
        if (skus.length === 0) {
          result.push(copySpu);
        } else {
          copySpu.skuInfo = copySpu.skuInfo.filter((sku) =>
            skus.some((s) => s.skuId === sku.skuId),
          );
          result.push(copySpu);
        }
      } else {
        result.push(copySpu);
      }
    }
  }

  return result;
};

const getOrderedSpec = (source: Source[], orderedIds: string[]): ItemSelectV2.Item[] => {
  const simpleMap = source.reduce((acc, cur) => {
    acc[cur.itemInternalId] = cur;
    return acc;
  }, {} as Record<string, Source>);

  return getSpec(orderedIds.map((id) => simpleMap[id]));
};

/**
 * 按需加载商品数据，这里迎合排序, 如果需要排序，强制从后端获取所有ids的信息
 */
export const getItemsByInternalIds: (
  source: Source[],
  orderBy?: '1' | '2' | '3' | '4',
) => Promise<ItemSelectV2.Item[]> = (source, orderBy) => {
  return new Promise((resolve, reject) => {
    if (source.length === 0) {
      return resolve([]);
    }
    const internalItemIds = source.map((item) => String(item.itemInternalId));
    const allIds = Object.keys(internalIdMap);
    const needIds = difference(internalItemIds, allIds);

    if (orderBy || needIds.length > 0) {
      queryItemListByInternalItemIds(orderBy ? internalItemIds : needIds, orderBy)
        .then((res) => {
          const items = res.itemList;
          cacheItems(items);
          if (orderBy) {
            resolve(
              getOrderedSpec(
                source,
                items.map((item) => item.internalItemId),
              ),
            );
          } else {
            resolve(getSpec(source));
          }
        })
        .catch(reject);
    } else {
      return resolve(getSpec(source));
    }
  });
};

export const cacheItems = (items: ItemSelectV2.Item[]) => {
  items.forEach((item) => {
    internalIdMap[item.internalItemId] = item;
  });
};

/**
 * how to use
 * useUnmount(() => {
 *   detachCache()
 * })
 */
export const detachCache = () => {
  internalIdMap = {};
};
